import { WORKFLOW_PRIMARY_LANGUAGE, WORKFLOW_TRANSLATION_LANGUAGES, type WorkflowTranslationLanguage } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Dialog, DialogContent, EmptyState } from "@sagot/ui";
import { useState } from "react";
import type { Definition } from "../types";
import { missingCount, translatableStrings } from "../translations-model";
import { LanguageSection } from "./language-section";

const T = "engage.workflows.translations";

interface TranslationsPanelProps {
  workflowId: string;
  definition: Definition;
  disabled: boolean;
  onChange: (definition: Definition) => void;
  /** Drafted texts were merged into the definition: the builder forgets what could be undone before them. */
  onDraftApplied?: () => void;
}

interface TranslationsDialogProps extends TranslationsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCloseAutoFocus?: (event: Event) => void;
}

/**
 * The builder's More menu → Translations modal: per language, every text the visitor reads next to its translation.
 * What has no translation falls back to the primary text. Edits go to the builder's draft (saved with it).
 */
export function TranslationsDialog({ open, onOpenChange, onCloseAutoFocus, ...panel }: TranslationsDialogProps) {
  const { t } = useTranslation();
  // Texts drafted by the model and not touched since: marked until the author edits them (kept while the modal is closed).
  const [drafted, setDrafted] = useState<ReadonlySet<string>>(new Set());
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t(`${T}.title`)} description={t(`${T}.subtitle`)} closeLabel={t("common.close")} className="max-w-2xl" onCloseAutoFocus={onCloseAutoFocus}>
        <TranslationsPanel {...panel} drafted={drafted} onDraftedChange={setDrafted} />
      </DialogContent>
    </Dialog>
  );
}

interface PanelState {
  drafted: ReadonlySet<string>;
  onDraftedChange: (update: (current: ReadonlySet<string>) => ReadonlySet<string>) => void;
}

function TranslationsPanel({ workflowId, definition, disabled, onChange, onDraftApplied, drafted, onDraftedChange: setDrafted }: TranslationsPanelProps & PanelState) {
  const { t } = useTranslation();
  const strings = translatableStrings(definition);
  return (
    <div className="space-y-4 text-sm">
      <div className="flex items-center justify-between rounded-md border border-line p-3">
        <span className="text-sm font-semibold">{t(`${T}.primary`, { language: t(`${T}.languages.${WORKFLOW_PRIMARY_LANGUAGE}`) })}</span>
        <Badge tone="success">{t(`${T}.complete`)}</Badge>
      </div>
      {strings.length === 0 ? (
        <EmptyState icon="languages" headingLevel={3} title={t(`${T}.emptyTitle`)} description={t(`${T}.emptyBody`)} />
      ) : (
        WORKFLOW_TRANSLATION_LANGUAGES.map((language: WorkflowTranslationLanguage) => (
          <LanguageSection
            key={language}
            workflowId={workflowId}
            language={language}
            definition={definition}
            strings={strings}
            missing={missingCount(definition, language)}
            drafted={drafted}
            disabled={disabled}
            onChange={onChange}
            onDraftApplied={onDraftApplied}
            onDrafted={(keys) => { setDrafted((current) => new Set([...current, ...keys.map((key) => `${language}:${key}`)])); }}
            onEdited={(key) => { setDrafted((current) => new Set([...current].filter((item) => item !== `${language}:${key}`))); }}
          />
        ))
      )}
      <p className="text-xs text-fg-subtle">{t(`${T}.savedWithDraft`)}</p>
    </div>
  );
}
